import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  createActivityChart,
  type ActivityChart,
  type ActivityChartData,
  type ActivityChartOptions,
  type ActivitySelection,
  type BarHeightMode,
  type ChartHover,
  type ChartIcons,
  type ChartMode,
  type ChartStrings,
  type ChartView,
  type ChartVisibility,
} from 'tacho-activity-chart';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { buildActivityChartStrings } from './chart-strings';

/**
 * Thin Angular host for the framework-agnostic tacho-activity-chart factory.
 *
 * The chart owns its SVG and ResizeObserver; this component only forwards
 * inputs and lifecycle events. Report-specific data is passed inside the
 * chart's data model (`ChartDay.annotations`), never as projected HTML.
 */
@Component({
  selector: 'tacho-activity-chart',
  template: '<div #host class="tacho-activity-chart-host"></div>',
  styles: [`
    :host {
      display: block;
      width: 100%;
    }

    .tacho-activity-chart-host {
      width: 100%;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TachoActivityChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('host', { static: true })
  host!: ElementRef<HTMLDivElement>;

  @Input()
  data!: ActivityChartData;

  /**
   * IANA timezone used for every clock and day boundary. Leave empty to follow
   * the user's browser timezone; the chart never falls back to UTC silently.
   */
  @Input()
  timezone = '';

  @Input()
  mode: ChartMode = 'stacked';

  /**
   * Which endpoint the data came from. Driver data labels the no-card split as
   * "With tachograph" / "Without tachograph" instead of the vehicle card.
   */
  @Input()
  dataSource?: 'driver' | 'vehicle';

  @Input()
  theme: ActivityChartOptions['theme'] = 'light';

  /** Project-standard activity icons used by the totals strip. */
  @Input()
  icons: ChartIcons = {
    driving: 'assets/kormany_new_colored.svg',
    work: 'assets/work_new_colored.svg',
    availability: 'assets/available_colored.svg',
    rest: 'assets/bed_new_colored.svg',
  };

  @Input()
  locale = 'en-GB';

  /**
   * Optional label overrides. Every label is already translated through the
   * site's `TranslateService` (`TachoActivityChart.*` keys), so this input is
   * only needed when a view wants different wording.
   */
  @Input()
  strings?: Partial<ChartStrings>;

  @Input()
  ariaLabel?: string;

  @Input()
  barHeightMode: BarHeightMode = 'specific';

  @Input()
  navigator = true;

  @Input()
  toolbar = true;

  @Input()
  legend = true;

  @Input()
  showSegmentLabels = true;

  @Input()
  showDayMeta = true;

  @Input()
  showRulers = true;

  @Input()
  wheelZoom = false;

  @Input()
  interactive = true;

  @Input()
  minWidth = 760;

  @Input()
  laneHeight = 70;

  @Input()
  visibility?: Partial<ChartVisibility>;

  @Output()
  selectionChange = new EventEmitter<ActivitySelection | null>();

  @Output()
  hoverChange = new EventEmitter<ChartHover | null>();

  @Output()
  viewChange = new EventEmitter<ChartView>();

  private chart?: ActivityChart;
  private initialized = false;
  private readonly langChangeSubscription: Subscription;

  constructor(private readonly translate: TranslateService) {
    // Labels follow the site language without recreating the chart.
    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      if (this.chart) {
        this.chart.update({ strings: this.resolvedStrings });
      }
    });
  }

  ngAfterViewInit(): void {
    this.initialized = true;
    this.ensureChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized) return;

    if (!this.chart) {
      this.ensureChart();
      return;
    }

    const patch: Partial<ActivityChartOptions> = {};
    if (changes['data']) patch.data = this.data;
    if (changes['timezone']) patch.timezone = this.resolvedTimezone;
    if (changes['mode']) patch.mode = this.mode;
    if (changes['dataSource']) {
      patch.dataSource = this.dataSource;
      patch.strings = this.resolvedStrings;
    }
    if (changes['theme']) patch.theme = this.theme;
    if (changes['icons']) patch.icons = this.icons;
    if (changes['locale']) patch.locale = this.locale;
    if (changes['strings']) patch.strings = this.resolvedStrings;
    if (changes['ariaLabel']) patch.ariaLabel = this.ariaLabel;
    if (changes['barHeightMode']) patch.barHeightMode = this.barHeightMode;
    if (changes['navigator']) patch.navigator = this.navigator;
    if (changes['toolbar']) patch.toolbar = this.toolbar;
    if (changes['legend']) patch.legend = this.legend;
    if (changes['showSegmentLabels']) patch.showSegmentLabels = this.showSegmentLabels;
    if (changes['showDayMeta']) patch.showDayMeta = this.showDayMeta;
    if (changes['showRulers']) patch.showRulers = this.showRulers;
    if (changes['wheelZoom']) patch.wheelZoom = this.wheelZoom;
    if (changes['interactive']) patch.interactive = this.interactive;
    if (changes['minWidth']) patch.minWidth = this.minWidth;
    if (changes['laneHeight']) patch.laneHeight = this.laneHeight;
    if (changes['visibility']) patch.visibility = this.visibility;

    if (Object.keys(patch).length > 0) {
      this.chart.update(patch);
    }
  }

  ngOnDestroy(): void {
    this.langChangeSubscription.unsubscribe();
    this.chart?.destroy();
    this.chart = undefined;
  }

  /**
   * The user's timezone from the input, or the browser timezone when the host
   * does not pass one. Reports always render in the viewer's local time.
   */
  private get resolvedTimezone(): string {
    const value = (this.timezone || '').trim();
    if (value) return value;
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  }

  /**
   * Complete label set for the current site language. The optional `strings`
   * input is merged last so a view can still override individual labels.
   */
  private get resolvedStrings(): ChartStrings {
    const strings = buildActivityChartStrings((key) => this.translate.instant(key));

    if (this.dataSource === 'driver') {
      // The driver endpoint has no vehicle card slot, so "no card" means the
      // tachograph did not attribute the time.
      strings.withDriverCard = strings.withTachograph;
      strings.withoutDriverCard = strings.withoutTachograph;
    }

    return { ...strings, ...(this.strings ?? {}) };
  }

  private ensureChart(): void {
    if (this.chart || !this.data) return;

    this.chart = createActivityChart(this.host.nativeElement, {
      data: this.data,
      timezone: this.resolvedTimezone,
      mode: this.mode,
      dataSource: this.dataSource,
      theme: this.theme,
      icons: this.icons,
      locale: this.locale,
      strings: this.resolvedStrings,
      ariaLabel: this.ariaLabel,
      barHeightMode: this.barHeightMode,
      navigator: this.navigator,
      toolbar: this.toolbar,
      legend: this.legend,
      showSegmentLabels: this.showSegmentLabels,
      showDayMeta: this.showDayMeta,
      showRulers: this.showRulers,
      wheelZoom: this.wheelZoom,
      interactive: this.interactive,
      minWidth: this.minWidth,
      laneHeight: this.laneHeight,
      visibility: this.visibility,
      onSelect: (selection) => this.selectionChange.emit(selection),
      onHover: (hover) => this.hoverChange.emit(hover),
      onViewChange: (view) => this.viewChange.emit(view),
    });
  }
}
